import { Source } from "@opendash/core/dist/types-generated";
import { useTranslation } from "@opendash/i18n";
import { ScheduleDataObjectType, scheduleIterator } from "@opendash/schedule";
import { Flex, Tag } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { observer } from "mobx-react-lite";
import { Relation } from "parse";
import React, { useEffect, useState } from "react";
import { NextScheduleTimeModal } from "..";
import {
  Schedule_Execution,
  Schedule_Step,
  Schedule_Template,
} from "../../../js/parsegenerated";
import { OpenService } from "../../../types/config";

type Props = {
  schedule: Schedule_Execution;
};

export default observer((props: Props) => {
  const t = useTranslation();
  const [nextScheduleModalOpen, setNextScheduleModalOpen] =
    useState<boolean>(false);
  const [nextScheduleDateChanged, setNextScheduleDateChanged] =
    useState<Boolean>(false);
  const [nextPlannedExecutionDate, setNextPlannedExecutionDate] =
    useState<Dayjs>();

  useEffect(() => {
    // const user = Parse.User.current();
    if (!props.schedule) return;

    //Set next schedule date
    const originSchedule = props.schedule.get("origin");
    if (originSchedule === undefined) {
      return;
    }

    const scheduleData: ScheduleDataObjectType = originSchedule.get("cron");

    console.log("Schedule Data:", scheduleData);

    // Handle different schedule types
    let nextDate: Date | undefined;

    if (scheduleData?.schedule_type === "timestamp" && scheduleData.timestamp) {
      // For timestamp-based schedules, calculate next date manually
      const { number, unit, startDate } = scheduleData.timestamp;
      let calculatedDate = dayjs(startDate);
      const now = dayjs();

      // Keep adding intervals until we get a future date
      while (calculatedDate.isBefore(now) || calculatedDate.isSame(now)) {
        calculatedDate = calculatedDate.add(number, unit as any);
      }

      nextDate = calculatedDate.toDate();
    } else {
      // For traditional cron schedules
      nextDate = scheduleIterator(scheduleData).nextDate(
        scheduleData?.timestamp?.startDate
      );
    }

    console.log("Next Date calculated:", nextDate);

    if (!nextDate) {
      setNextPlannedExecutionDate(undefined);
      return;
    }
    setNextPlannedExecutionDate(dayjs(nextDate));
  }, [props.schedule]);

  return (
    <>
      <Flex wrap="nowrap" align="start" justify="start" gap="small">
        <Tag
          onClick={() => setNextScheduleModalOpen((prev) => !prev)}
          style={{ cursor: "pointer" }}
        >
          {t("maintenance:schedule.step.execute.next_schedule", {
            date: nextPlannedExecutionDate?.format(OpenService.Formats.date),
          })}
        </Tag>

        {nextScheduleDateChanged ? (
          <Tag color="warning">
            {t("maintenance:schedule.step.execute.next_schedule_changed", {
              date: nextPlannedExecutionDate?.format(OpenService.Formats.date),
            })}
          </Tag>
        ) : null}
      </Flex>

      <NextScheduleTimeModal
        schedule={props.schedule}
        nextPlannedExecutionDate={nextPlannedExecutionDate}
        open={nextScheduleModalOpen}
        onOk={async (
          values: ScheduleDataObjectType,
          nextIntervalSwitch: number,
          createNewSchedule: boolean
        ) => {
          // Based on the selected value, set the next schedule date
          // 0: Continue based on the original schedule
          // 1: Move the schedule by difference --> disable origin schedule and create a new one.
          // 2: Individually --> disable origin schedule and create a new one.
          if (createNewSchedule && nextIntervalSwitch === 1) {
            const origin: Schedule_Template = props.schedule.get("origin");
            const steps: Schedule_Step[] = (await origin
              .relation("steps")
              .query()
              .find()) as Schedule_Step[];
            const sources: Source[] = (await origin
              .relation("sources")
              .query()
              .find()) as Source[];
            const stepsRelation = new Relation<
              Schedule_Template,
              Schedule_Step
            >();
            const sourcesRelation = new Relation<Schedule_Template, Source>();

            for (const step of steps) {
              stepsRelation.add(step);
            }

            for (const source of sources) {
              sourcesRelation.add(source);
            }

            const scheduletemplate = new Schedule_Template({
              title: props.schedule.get("title"),
              enabled: true,
              description: props.schedule.get("description"),
              steps: stepsRelation,
              sources: sourcesRelation,
              cron: values,
            });

            await scheduletemplate.save();

            origin.set("enabled", false);
            await origin.save();

            setNextScheduleDateChanged(true);
          }

          setNextScheduleModalOpen(false);
        }}
        onCancel={() => {
          setNextScheduleModalOpen(false);
        }}
      />
    </>
  );
});
